<template>
  <v-container class="archive pa-0 ma-0" fluid>
    <v-card class="mb-2">
      <v-card-title>
        <v-toolbar flat style="cursor: default;">
          <v-toolbar-title class="ml-2 title"><v-icon left>mdi-file-upload-outline</v-icon>
            {{ $route.name }} Files
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            id="downloadBtn"
            color="info"
            @click="downloadTemplate">
            <v-icon>mdi-file-download-outline</v-icon>
            Download DSD Template
          </v-btn>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-btn
            id="resetBtn"
            color="warning"
            @click="reset">
            Clear Data
          </v-btn>
        </v-toolbar>
      </v-card-title>
      <v-card-text>
        <v-container fluid>
          <v-form ref="form" lazy-validation v-model="valid">
            <v-row>
              <v-col>
                <v-file-input
                  v-model="file"
                  accept=".xlsx"
                  outlined
                  truncate-length="30"
                  dense
                  hide-details="auto"
                  label="DSD Item File (.xlsx format)"
                  :clearable="false"
                  @change="handleFile">
                </v-file-input>
              </v-col>
              <v-col>
                <VendorSelect
                  v-if="$auth.tenant === 'alliance-retail-group'"
                  :isDSDupload="true"
                  :selectedVendor.sync="selectedVendor">
                </VendorSelect>
                <template v-if="$auth.tenant === 'awg'">
                  <v-select
                    v-model="selectedSources"
                    label="Item Sources"
                    :items="uploadSources"
                    item-text="source_name"
                    item-value="source_id"
                    :menu-props="{ bottom: true, offsetY: true }"
                    multiple
                    deletable-chips
                    small-chips
                    dense
                    outlined
                    clearable
                    return-object>
                    <template v-slot:prepend-item>
                      <v-list-item
                        @click="toggle">
                        <v-list-item-action>
                          <v-checkbox
                            :value="allSources">
                          </v-checkbox>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>
                            Select All
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider></v-divider>
                    </template>
                  </v-select>
                </template>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
    </v-card>
    <v-card>
      <v-toolbar class="toolbar" flat>
        <v-btn 
          v-if="items.length > 0 && validateItems"
          outlined
          class="readonly-button"
          :color="errorItems > 1 ? 'red' : 'black'">
           Items with Errors: {{ errorItems }}
        </v-btn>
        <v-btn
          v-if="canEdit"
          :disabled="items.length == 0"
          class="blue darken-3 white--text mx-2"
          @click="downloadTableToFile">
          <v-icon class="mr-2">mdi-file-download-outline</v-icon>
          Download table to file
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          class="mx-3"
          :disabled="disableUpload"
          color="success"
          @click="verifyItems">
          Upload
        </v-btn>
      </v-toolbar>
      <v-data-table 
        id="dsdUploadTable"
        :height="maxTableHeight"
        :items="items"
        :headers="computedHeaders"
        :items-per-page="pageSize"
        :page="page"
        hide-default-footer
        fixed-header
        no-data-text="Select file to populate table">
        <template v-slot:[`item.warnings`]="{ item }">
          <v-tooltip v-if="item.isValid == false" color='black' top>
            <template v-slot:activator="{ on }">
              <v-badge 
                overlap
                color="orange"
                :content="item.invalid_messages.length">
                <v-icon
                  large
                  v-on="on"
                  color="red">
                  mdi-alert-circle-outline
                </v-icon>
              </v-badge>
            </template>
            <span v-html="formatMessages(item)"></span>
          </v-tooltip>
        </template>

        <!-- USED FOR AWG -->
        <template v-slot:[`item.delete_item`]="{ item }">
          <v-simple-checkbox
            v-if="canEdit"
            v-model="item.delete_item" 
            @input="updateItems">
          </v-simple-checkbox>
          <v-icon v-else color="red">
            {{ item.delete_item ? 'mdi-close-circle-outline' : ''}}
          </v-icon>
        </template>

        <!-- USED FOR ARG -->
        <template v-slot:[`item.item_action`]="{ item }">
          <v-chip :color="getChipColor(item.item_action)">
            <span
              style="
                display: inline-block;
                margin: 0 auto;
                width: 100px;
                text-align: center">
                {{ item.item_action }} 
              </span>
          </v-chip>
        </template>

        <template #[`item.upc`]="{ item }">
          <EditDialog
            title="Update UPC"
            :value.sync="item.upc"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template #[`item.description`]="{ item }">
          <EditDialog
            title="Update Description"
            :value.sync="item.description"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template #[`item.item_id`]="{ item }">
          <EditDialog
            title="Update Item ID"
            :value.sync="item.item_id"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template #[`header.department`]="{ header }">
          <div>
            {{ header.text }}
            <v-tooltip right color="black">
              <template #activator="{ on, attrs }">
                <v-icon small color="white" class="ml-2" v-bind="attrs" v-on="on">mdi-alert-circle-outline</v-icon>
              </template>
              <span v-html="departmentNames"></span>
            </v-tooltip>
          </div>
        </template>

        <template #[`item.department`]="{ item }">
          <EditDialog
            title="Update Department"
            :value.sync="item.department"
            :items="departmentList"
            :isDropdown="true"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template #[`item.vendor_id`]="{ item }">
          <EditDialog
            title="Update Vendor ID"
            :value.sync="item.vendor_id"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template #[`item.vendor_name`]="{ item }">
          <EditDialog
            title="Update Vendor Name"
            :value.sync="item.vendor_name"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template #[`item.pack_size`]="{ item }">
          <EditDialog
            title="Update Pack Size"
            :value.sync="item.pack_size"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template v-slot:[`item.sale_price`]="{ item }">
          <EditDialog
            title="Update Sale Price"
            :value.sync="item.sale_price"
            :isPrice="true"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template v-slot:[`item.cost`]="{ item }">
          <EditDialog
            title="Update Cost"
            :value.sync="item.cost"
            :isPrice="true"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template #[`item.category`]="{ item }">
          <EditDialog
            title="Update Category"
            :value.sync="item.category"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template #[`item.subcategory`]="{ item }">
          <EditDialog
            title="Update Sub Category"
            :value.sync="item.subcategory"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template #[`item.qty_per_case`]="{ item }">
          <EditDialog
            title="Update Qty Per Case"
            :value.sync="item.qty_per_case"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template v-slot:[`item.future_cost`]="{ item }">
          <EditDialog
            title="Update Future Cost"
            :value.sync="item.future_cost"
            :isPrice="true"
            :clearable="true"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template #[`item.future_cost_date`]="{ item }">
          <EditDialog
            title="Update Future Cost Date"
            :value.sync="item.future_cost_date"
            :isDate="true"
            :clearable="true"
            :canEdit="canEdit"
            @save="updateItems">
          </EditDialog>
        </template>

        <template v-slot:footer>
          <PagingFooter
            :pageSizes="pageSizes"
            :pageSize.sync="pageSize"
            :page="page"
            :disablePrevious="disablePrevious"
            :disableNext="disableNext"
            @previous="previousPage"
            @next="nextPage">
          </PagingFooter>
        </template>
      </v-data-table>
    </v-card> 
    <DSDVerifyModal
      v-if="showVerifyModal"
      :value.sync="showVerifyModal"
      :headers="dsdHeaders"
      :items="items"
      :selectedSources="selectedSources" 
      @submit="uploadFile"/>
  </v-container>
</template>
<script lang="js">
// components
import VendorSelect from '@/components/VendorSelect'

// api
import dsd from '@/api/dsd.js'
import Items from '@/api/endpoints'

// Components
import PagingFooter from '@/components/PagingFooter.vue'
import DSDVerifyModal from '@/components/items/DSDVerifyModal.vue'
import EditDialog from '@/components/items/edit_dialog.vue'

// mixins
import { displayAlert } from '@/mixins/alert'
import { userAccess } from '@/mixins/user-access'
import { fullWidthTable } from '@/mixins/table'

// 3rd party
import * as XLSX from "xlsx"

export default {
  name: 'DSD-Upload',
  data () {
    return {
      selectedSources: [],
      selectedVendor: null,
      fullList: null,
      file: null,
      filename: '',
      valid: true,
      loading: false,
      noShowSources: ['ft_scott', 'ft_worth', 'vmc'],
      items: [],
      showVerifyModal: false,
      maxTableHeight: 450,
      page: 1,
      pageSize: 10,
      pageSizes: [5, 10, 25, 50],
      departmentList: [],
      canEdit: false,
      limitOneFile: true,
      skipValidationTenants: ['alliance-retail-group'],
      awg_headers: [
        { text: "Delete Item", value: "delete_item", align: "center" },
        { text: "UPC w/o Check Digit", value: "upc", align: "center", required: true },
        { text: "Description", value: "description", align: "start" },
        { text: "Item ID", value: "item_id", align: "center", required: true },
        { text: "Department", value: "department", align: "center" },
        { text: "Vendor ID", value: "vendor_id", align: "center" },
        { text: "Vendor Name", value: "vendor_name", align: "center" },
        { text: "Pack Size", value: "pack_size", align: "center" },
        { text: "Sale Price", value: "sale_price", align: "center" },
        { text: "Cost", value: "cost", align: "center" },
        { text: "Category", value: "category", align: "center" },
        { text: "SubCategory", value: "subcategory", align: "center" },
        { text: "Qty Per Case", value: "qty_per_case", align: "center" },
        { text: "Future Cost", value: "future_cost", align: "center" },
        { text: "Future Cost Date", value: "future_cost_date", align: "center" }
      ],
      arg_headers: [
        { text: "Item Action", value: "item_action", align: "center" },
        { text: "UPC w/o Check Digit", value: "upc", align: "center", required: true },
        { text: "Description", value: "description", align: "start" },
        { text: "Pack Size", value: "pack_size", align: "center" },
        { text: "Case Cost", value: "cost", align: "center" },
        { text: "Regular Retail", value: "sale_price", align: "center" },
        { text: "Qty Per Case", value: "qty_per_case", align: "center" },
        { text: "Future Cost", value: "future_cost", align: "center" },
        { text: "Future Cost Date", value: "future_cost_date", align: "center" }
      ],
      validationRules: {
        awg: { 
          minUPCLength: 10,
          itemIdLength: 8
        }
      }
    }
  },
  components: { PagingFooter, DSDVerifyModal, EditDialog, VendorSelect },
  mixins: [displayAlert, userAccess, fullWidthTable],
  watch: {
    pageSize: {
      handler (newValue) {
        this.page = 1
      }
    }
  },
  async created () {
    await this.$store.dispatch('setSources')
    this.departmentList = await Items.getItemDepartments()

    if (this.items.length > 0) {
      this.updateItems()
    }   
  },
  computed: {
    disableUpload () {
      return this.items.length == 0 || this.loading || this.errorItems > 0 || ((this.$auth.tenant === 'alliance-retail-group' && !this.selectedVendor) || (this.$auth.tenant === 'awg' && this.selectedSources.length === 0))
    },
    uploadSources() {
      return this.userSources.filter((source) => !this.noShowSources.includes(source.source_id))
    },
    allSources() {
      return this.selectedSources.length === this.uploadSources.length
    },
    dsdHeaders() {
      if (this.$auth.tenant === 'awg') return this.awg_headers
      else if (this.$auth.tenant === 'alliance-retail-group') return this.arg_headers
    },
    disablePrevious () {
      return this.page === 1
    },
    disableNext () {
      return (this.items.length < this.pageSize || this.pageSize * this.page >= this.items.length)
    },
    computedHeaders () {
      if (this.validateItems) {
        const warning_space = { align: 'center', sortable: true, text: '', value: 'warnings' }
        const altered = [ warning_space, ...this.dsdHeaders ]
        return altered.flatMap(item => {
          return { ...item, class: 'accent white--text text-no-wrap', sortable: false }
        })
      } else {
        return this.dsdHeaders.flatMap(item => {
          return { ...item, class: 'accent white--text text-no-wrap', sortable: false }
        })
      }
      
    },
    errorItems () {
      return this.items.filter(item => item.isValid == false).length
    },
    departmentNames () {
      return "The following are valid department names.<br>These names are case sensitive:<br><br>" + this.departmentList.map(dept => `${dept.department_name}`).join('<br>')
    },
    validateItems () {
      return !this.skipValidationTenants.includes(this.$auth.tenant)
    },
    rules () {
      return this.validationRules[this.$auth.tenant]
    }
  },
  methods: {
    nextPage () {
      this.page += 1
    },
    previousPage () {
      if (this.page > 1) {
        this.page -= 1
      }
    },
    async downloadTemplate () {
      try {
        this.emitAlert(true, 'info', "Template downloading")
        const response = await dsd.getTemplate()
        const url = response.data.url
        window.open(url, '_blank')
      } catch (err) {
        this.emitAlert(true, 'error', "Error while downloading template")
        console.log(err)
      }
    },
    processSources() {
      const source_keys = this.selectedSources.map(source => source.source_id)
      return source_keys.join(",")
    },
    reset () {
      this.$refs.form.reset()
      this.file = null
      this.filename = ''
      this.selectedVendor = null
      this.items = []
    },
    handleFile(file) {
      if (file == null) { return }

      if (!file.name.includes(".xlsx")) {
        this.file = null
        this.emitAlert(true, 'warning', 'Please upload a .xlsx file')
        return
      }

      const reader = new FileReader()

      try {
        reader.onload = (e) => {
          this.file.upload_file = e.target.result
          this.filename = this.file.name
          this.loadFileItemsToTable()
          this.updateItems()
        }
        reader.readAsArrayBuffer(file)
      } catch (error) {
        console.log(error)
      }
    },
    async uploadFile () {
      if (this.$auth.tenant === 'alliance-retail-group' && !this.selectedVendor) {
        this.emitAlert(true, 'error', 'Please select a vendor')
        return
      } else if (this.$auth.tenant != 'alliance-retail-group' && this.selectedSources.length === 0) {
        this.emitAlert(true, 'error', 'Please select at least one source')
        return
      }

      try {
        this.emitAlert(true, 'info', 'Validating and Uploading file...')
        const file = this.file
        const blob = new Blob([file.upload_file], { type: `${file.type}` })
        const formData = new FormData()

        formData.append('name', `${file.name}`)
        formData.append('upload_file', blob, `${file.name}`)
        if (this.$auth.tenant === 'alliance-retail-group') {
          formData.append('vendor_id', `${this.selectedVendor.attributes.VENDOR_ID}`)
        } else {
          formData.append('sources', `${this.processSources()}`)
        }
        let response = await dsd.uploadDSDFile(formData)
        console.log(response.data)

        // Check that the upload worked
        if (response.data.status != "success") {
          throw new Error(response.data.error)
        }

        let successMsg = "Upload complete. \nYou will recieve an email when the process has completed successfully."
        this.emitAlert(true, 'success', successMsg)
        this.reset()
      } catch (err) {
        console.error(err)
        this.emitAlert(true, 'error', err)
        throw err
      }
    },
    toggle() {
      if (this.allSources) {
        this.selectedSources = []
      } else {
        this.selectedSources = this.uploadSources
      }
    },
    loadFileItemsToTable() {
      const workbook = XLSX.read(this.file.upload_file, { type: "binary", cellDates: true, dateNF: 'mm/dd/yyyy' })
      const sheetNames = workbook.SheetNames
      const name = sheetNames[0]
      console.log(name)
      if (!['AWG DSD Item Maintenance File', 'AWG DSD Saved Changes', 'ARG DSD Item Maintenance File'].includes(name)) {
        this.emitAlert(true, 'error', "Selected file isn't using the DSD template file. Please download and use the template file for uploading to this page.")
        this.reset()
        return
      }
      const sheet = workbook.Sheets[name]

      // Default values are for typical AWG run
      let startAt = 8
      let firstHeader = "Delete Item"
      if (name == 'AWG DSD Saved Changes') {
        startAt = 0
      } else if (this.$auth.tenant == 'alliance-retail-group') {
        startAt = 2
        firstHeader = "Item Action"
      }

      // Check that the contents of the file are valid
      let data = XLSX.utils.sheet_to_json(sheet, { raw: false, range: startAt, header: 1 })
      let dataIsValid = this.validateFile(data)
      if (!dataIsValid) {
        this.reset()
        return
      }

      // Gather data into 2D array
      data = data.filter(row => (row.length > 0) && row[0] !== firstHeader) 
      let newItems = []
      
      for (const row of data) {
        let item = {}
        for (const [index, field] of row.entries()) {
          // Delete Item is entered as 'Y' or blank in excel form - need to handle to convert to bool
          if (this.dsdHeaders[index].value == 'delete_item') {
            if (field != null && field.toLowerCase() == 'y') {
              item[this.dsdHeaders[index].value] = true
            } else { 
              item[this.dsdHeaders[index].value] = false 
            }
          } else {
            item[this.dsdHeaders[index].value] = field
          }
        }

        // Put in any missing headers and give them null value - prevents undefined issues
        for (const header of this.dsdHeaders) {
          if (!item.hasOwnProperty(header.value)) {
            item[header.value] = null
          }
        }
        item = { ...item, isValid: true, invalid_messages: [] }
        newItems.push(item)
      }

      // If only one file should be loaded at a time, only show the items from that file. Other wise, build up table
      if (this.limitOneFile) {
        this.items = newItems
      } else {
        this.items = [ ...this.items, ...newItems ]
      }
    },    
    updateItems () {
      if (this.validateItems) {
        this.checkItemValues()
      }
    },
    checkItemValues () {
      // Do a check over all items currently loaded into the table
      this.loading = true

      for (const item of this.items) {
        // Reset item
        item.isValid = true
        item.invalid_messages = []

        this.checkUPC(item)
        this.checkItemID(item)
        this.checkDepartment(item)
        this.checkFutureFields(item)
      }

      this.checkForDuplicates("upc", "UPC")
      this.checkForDuplicates("item_id", "Item ID")

      this.loading = false
    },
    checkUPC (item) {
      // Check if UPC exists
      if (this.isEmpty(item.upc)) {
        this.invalidateItem(item, "A UPC value is required")
        console.log("Empty UPC")
      } else {
        // UPC exists, validate UPC value
        if (item.upc.includes(" ")) {
          this.invalidateItem(item, "UPC cannot contain spaces in value")
        }

        if (item.upc.length < this.rules.minUPCLength) {
          this.invalidateItem(item, `UPC needs to be at least ${this.rules.minUPCLength} digits`)
        }

        if (item.upc.match(/[a-z]/i)) {
          this.invalidateItem(item, 'Only digits are allowed in UPC values')
        }
      }
    },
    checkItemID (item) {
      // Check if Item ID exists
      if (this.isEmpty(item.item_id)) {
        this.invalidateItem(item, "An Item ID is required")
      } else {
        if (item.item_id.includes(" ")) {
          this.invalidateItem(item, "Item ID cannot contain spaces in value")
        }

        if (item.item_id.length != this.rules.itemIdLength) {
          this.invalidateItem(item, `Item ID needs to be ${this.rules.itemIdLength} characters long`)
        }

        if (!item.item_id.match(/[D][0-9]*/g)) {
          this.invalidateItem(item, "Item ID must start with a 'D'")
        }
      }
    },
    checkDepartment (item) {
      // Validate department name only if it exists
      if (this.isEmpty(item.department)) return

      if (!this.departmentList.map(dept => dept.department_name).includes(item.department)) {
        this.invalidateItem(item, "Department value is not valid")
      }
    },
    checkFutureFields (item) {
      // If Future Cost or Future Cost date exist, the other needs to exist as well
      if (this.isEmpty(item.future_cost) && !this.isEmpty(item.future_cost_date)) {
        this.invalidateItem(item, "Must include a Future Cost if Future Cost Date is present")
      }

      if (!this.isEmpty(item.future_cost) && this.isEmpty(item.future_cost_date)) {
        this.invalidateItem(item, "Must include a Future Cost Date if Future Cost is present")
      }
    },
    checkForDuplicates (field, name) {
      const fullList = this.items.flatMap(item => { return item.delete_item ? null : item[field] })
      for (const item of this.items) {
        let amount = fullList.filter(value => item[field] == value).length
        if (amount > 1) {
          this.invalidateItem(item, `This ${name} is included on more than one item`)
        }
      }
    },
    verifyItems() {
      if (this.validateItems) {
        this.showVerifyModal = true
      } else {
        this.uploadFile()
      }
    },
    invalidateItem (item, message) {
      item.isValid = false
      item.invalid_messages.push(message)
    },
    formatMessages (item) {
      let message_string = ''
      for (let message of item.invalid_messages) {
        message_string += "- " + message + "<br />"
      }
      return message_string
    },
    isEmpty (value) {
      return value == null || value.trim() == ''
    },
    async downloadTableToFile() {
      // Currently unused due to editing items being disabled
      console.log("Downloading table")
      let filteredItems = []
      
      // Remove validation fields from items to prep for download
      for (const item of this.items) {
        const { isValid, invalid_messages, ...filteredItem } = item
        if (filteredItem.delete_item == false) {
          filteredItem.delete_item = ''
        } else {
          filteredItem.delete_item = 'Y'
        }
        filteredItems.push(filteredItem)
      }

      const headers = this.dsdHeaders.map(header => header.text)

      let newSheet = XLSX.utils.json_to_sheet(filteredItems)
      XLSX.utils.sheet_add_aoa(newSheet, [headers], {origin: "A1"})
      let workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, newSheet, "AWG DSD Saved Changes")
      XLSX.writeFile(workbook, "DSD_Saved_Table.xlsx")      
    },
    validateFile(file_data) {
      let data = file_data.filter(row => (row.length > 0) && row.every(cell => Boolean(cell))) // Clear out any empty rows from the sheet
      
      // Check to make sure they're using the template, and the data is starting where expected. 
      if (data.length == 0 || (this.$auth.tenant === 'alliance-retail-group' && data[0][0] !== "Item Action") || (this.$auth.tenant === 'awg' && data[0][0] !== "Delete Item")) {
        let errString = "Uploaded file is not using the template as its base. Please download the template and do not remove the header rows."
        console.log(errString)
        this.reset()
        this.emitAlert(true, 'error', errString)
        return false
      }

      // Check to make sure they don't have any missing UPC's or item ID's
      //    AWG doesn't need this, does overall validation later
      if (this.$auth.tenant == 'alliance-retail-group') {
        for (const row of data) {
          if (row[0] == null || row[1] == null) {
            let errString = 'Uploaded file is missing required data (Item Action or UPC)'
            console.log(errString)
            this.reset()
            this.emitAlert(true, 'error', errString)
            return false
          }
        } 
      }

      // All checks passed, return true
      return true
    },
    getChipColor(value) {
      if (value == "Update Item") return "light-blue lighten-3"
      else if (value == "New Item") return "light-green lighten-2"
      else if (value == "Delete Item") return "red accent-1"
      else return "grey"
    }
  }
}
</script>
<style>
.tooltip-inner {
  text-align: left;
}

.readonly-button {
  pointer-events: none;
}
</style>


// WEBPACK FOOTER //
// src/views/upload.vue